/**
 * The selection create wizard of one kind: /colorselections/<kind>/new/<step>.
 *
 * The Setup step composes the selection's name from the laser, the substrate
 * and a tag; the selection itself is only created when Choose Explorations
 * is confirmed. Until then the choices are held here, in the state of this
 * layout route, and handed to the steps through the router's outlet context.
 * The draft lives as long as the wizard is open in this tab; a reload or a
 * bookmark of the second step starts the wizard over.
 */
import React, { useCallback, useState } from "react";
import { Navigate, Outlet, useLocation, useOutletContext } from "react-router-dom";
import { useSelectionParams } from "../../helper/entityRoutes";
import { projectIdFromSearch } from "../../helper/projects";

export const EMPTY_SELECTION_DRAFT = {
    laserId: "",
    laserName: "",
    materialId: "",
    materialName: "",
    description: "",
    tag: "",
    // The composed identifier (<Substrate>-<Laser>-<YYYYMMDD>-<Tag>).
    name: "",
    // The project the selection is filed in when the wizard was started
    // from a project's New menu (?project=<id>); null otherwise.
    projectId: null,
};

export default function SelectionSetupFlow() {
    const location = useLocation();
    const { setupRoute } = useSelectionParams();
    const [draft, setDraft] = useState(() => ({
        ...EMPTY_SELECTION_DRAFT,
        projectId: projectIdFromSearch(location.search),
    }));
    const updateDraft = useCallback((partial) => {
        setDraft((current) => ({ ...current, ...partial }));
    }, []);

    const firstStep = setupRoute("name");
    if (!draft.name && location.pathname !== firstStep) {
        return <Navigate to={firstStep} replace />;
    }
    return <Outlet context={{ draft, updateDraft }} />;
}

/** The wizard's draft and its updater, for the step pages. */
export function useSelectionDraft() {
    return useOutletContext();
}
